import { store } from 'src/Common/RTK/store';
import { addToast, ToastAction } from 'src/features/createAction/store/slices/ToastSlice';

/**
 * Toast utility functions for easy toast dispatching from anywhere in the app.
 * These functions can be used without requiring a component to be connected to Redux.
 */

export type ToastLevel = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
  message: string;
  subtitle?: string;
  level?: ToastLevel;
  scope?: string;
  ttlMs?: number;
  actions?: ToastAction[];
  close?: () => void;
  emphasizedText?: string;
}

/**
 * Show a success toast notification
 * @param message - The message to display
 * @param options - Additional options (scope, ttlMs)
 */
export const showSuccessToast = (
  message: string,
  options: Omit<ToastOptions, 'message' | 'level'> = {}
) => {
  const toastPayload = {
    message,
    subtitle: options.subtitle,
    level: 'success' as ToastLevel,
    scope: options.scope || 'global',
    ttlMs: options.ttlMs || 4000,
    actions: options.actions,
    close: options.close,
    emphasizedText: options.emphasizedText,
  };

  store.dispatch(addToast(toastPayload));
};

/**
 * Show an error toast notification
 * @param message - The message to display
 * @param options - Additional options (scope, ttlMs)
 */
export const showErrorToast = (
  message: string,
  options: Omit<ToastOptions, 'message' | 'level'> = {}
) => {
  const toastPayload = {
    message,
    level: 'error' as ToastLevel,
    scope: options.scope || 'global',
    ttlMs: options.ttlMs || 6000,
    close: options.close,
  };

  store.dispatch(addToast(toastPayload));
};

/**
 * Show a warning toast notification
 * @param message - The message to display
 * @param options - Additional options (scope, ttlMs)
 */
export const showWarningToast = (
  message: string,
  options: Omit<ToastOptions, 'message' | 'level'> = {}
) => {
  store.dispatch(
    addToast({
      message,
      level: 'warning',
      scope: options.scope || 'global',
      ttlMs: options.ttlMs || 5000,
    })
  );
};

/**
 * Show an info toast notification
 * @param message - The message to display
 * @param options - Additional options (scope, ttlMs)
 */
export const showInfoToast = (
  message: string,
  options: Omit<ToastOptions, 'message' | 'level'> = {}
) => {
  store.dispatch(
    addToast({
      message,
      level: 'info',
      scope: options.scope || 'global',
      ttlMs: options.ttlMs || 4000,
    })
  );
};

/**
 * Show a generic toast notification
 * @param options - Toast options including message and level
 */
export const showToast = (options: ToastOptions) => {
  store.dispatch(
    addToast({
      message: options.message,
      level: options.level || 'info',
      scope: options.scope || 'global',
      ttlMs: options.ttlMs || 4000,
    })
  );
};

/**
 * Example usage:
 *
 * import { showSuccessToast, showErrorToast } from 'src/Common/Utils/toastUtils';
 *
 * // Simple success toast
 * showSuccessToast('Action completed successfully!');
 *
 * // Error toast with custom scope and duration
 * showErrorToast('Failed to save changes', {
 *   scope: 'formValidation',
 *   ttlMs: 8000
 * });
 *
 * // Custom toast
 * showToast({
 *   message: 'Processing...',
 *   level: 'info',
 *   scope: 'dataProcessing',
 *   ttlMs: 10000
 * });
 */
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 3 lines modified | Forwarded emphasized toast text
